// Phases 1-4: turning the raw playbook markdown into structured sections and
// plan endings (no JSON assembly, no file generation). Branchpoints used to
// be extracted into their own stub nodes here; they now render in place as
// inline expandable boxes via the runtime `:::branchpoint` directive, so
// this file only repairs branchpoint fences and leaves the body untouched.
import {
  scanDirectiveDepthClose,
  isAnyDirectiveOpener,
  directiveOpen,
  findDirectiveCloseLine,
} from './directives'

// Opener test for plan-ending blocks: only these directives nest the count.
// Lives at module scope so both parsePlanEndings and splitPlanEndingsOff
// (which needs to find the close of the last plan-ending) can use it.
const isPlanEndingNester = (trimmed: string): boolean => /^:::(details|dateMarker|tabBoxes)/.test(trimmed)

// A plan-ending `details` opener at any fence depth. The converter out-fences
// a details block to 4+ colons when it wraps a nested 3-colon directive (e.g.
// the Plan S ending's `:::box`), so matching exactly `:::details` would miss it.
const isDetailsOpener = (trimmed: string): boolean => /^:{3,}details$/.test(trimmed)

// Closer scan for the plan-ending details block opened at `openLine` (whose
// title sits at `titleLineIdx`). A 3-colon fence is ambiguous — nested
// directives share the same bare `:::` closer — so it keeps the legacy
// depth-count scan. A deeper fence is unambiguous and is matched by fence
// length, so a nested `:::box`'s closer can't false-close the block.
function findPlanEndingClose(
  lines: string[],
  openLine: number,
  titleLineIdx: number,
): { closeLine: number; closed: boolean } {
  const fence = directiveOpen(lines[openLine])?.fence ?? ':::'
  if (fence.length === 3) return scanDirectiveDepthClose(lines, titleLineIdx + 1, isPlanEndingNester)
  const closeLine = findDirectiveCloseLine(lines, openLine)
  return closeLine === undefined ? { closeLine: lines.length, closed: false } : { closeLine, closed: true }
}

export type Section = { title: string; content: string; summary?: string }
export type SectionMeta = {
  id: string
  title: string
  filename: string
  epilogueTitle?: string
  summary?: string
}
// Retained as an empty placeholder so the assembler/orchestrator signatures
// don't have to change shape just because the array is always empty now.
// Branchpoint blocks render in place via the runtime `:::branchpoint`
// directive handler — they no longer become standalone graph nodes.
export type Branchpoint = Record<string, never>
export type PlanEnding = { planLetter: string; title: string; content: string }

// Result of phase 1: where the root/intro/2026-2028/2029 boundaries fell.
// `section2026Tail` / `hoistedSectionTitle` are set when the doc places the
// `# 2029: …` H1 (plus its dateMarker) *above* the "What happens in Plan…"
// blocks: that tail gets appended to the very end of the 2026-2028 chunk
// AFTER Scenario-tab injection (so it renders above the choose-your-path
// flowchart instead of being captured into the last Scenario tab), and the
// chunk generator strips the H1's duplicate from the matching Plan A
// section file.
export type DocBoundaries = {
  rootContent: string
  section2026_2028: string
  afterIntroSection: string
  planEndingsContent: string
  section2026Tail?: string
  hoistedSectionTitle?: string
}

export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/['']/g, '-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function trimContent(content: string): string {
  let s = content.replace(/^\n+/, '').replace(/\n+$/, '')
  if (!s.endsWith('\n')) s += '\n'
  return s
}

export function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// The diagram directive that opens the playbook root: `:::timelineDiagram`
// (renamed from the legacy `:::timeline` — both still recognized as the root
// boundary so already-split content and pasted markdown keep working).
const TIMELINE_BLOCK_REGEX = /^:{3,}(?:timelineDiagram|timeline)[ \t]*\n(?:[ \t]*\n)*:{3,}[ \t]*$/m
const DATE_MARKER_2029_REGEX = /^:{3,}dateMarker[ \t]*\n[ \t]*2029-01-01[ \t]*\n/m
const SCENARIO_2026_HEADING_REGEX = /^##\s+2026\b.*$/m
// Any dated `:::dateMarker` block (e.g. the 2026-01-01 scenario opener).
// Used as the root/scenario boundary when the doc has neither a
// `:::timelineDiagram` block nor a `## 2026…` scenario heading.
const ANY_DATE_MARKER_REGEX = /^:{3,}dateMarker[ \t]*\n[ \t]*\d{4}-\d{2}-\d{2}[ \t]*\n/m

// Splits `markdown` into root preamble vs. everything-after-the-root using
// (in priority order) a :::timelineDiagram block, the 2029 dateMarker's preceding
// scenario heading / first dated dateMarker / first H1, else the first H1.
function findRootBoundary(markdown: string): { rootContent: string; afterRoot: string } {
  const timelineMatch = markdown.match(TIMELINE_BLOCK_REGEX)
  if (timelineMatch && timelineMatch.index !== undefined) {
    const cut = timelineMatch.index + timelineMatch[0].length
    return { rootContent: markdown.substring(0, cut), afterRoot: markdown.substring(cut) }
  }
  const dateMarker2029InMarkdown = markdown.match(DATE_MARKER_2029_REGEX)
  if (dateMarker2029InMarkdown && dateMarker2029InMarkdown.index !== undefined) {
    return findRootBoundaryFrom2029(markdown, dateMarker2029InMarkdown.index)
  }
  const firstH1Match = markdown.match(/^# .+$/m)
  if (firstH1Match && firstH1Match.index !== undefined) {
    return { rootContent: markdown.substring(0, firstH1Match.index), afterRoot: markdown.substring(firstH1Match.index) }
  }
  throw new Error('Could not find :::timelineDiagram or first H1 heading')
}

// Root boundary when only the 2029 dateMarker anchors us: prefer the `## 2026…`
// scenario heading before it, then the first dated dateMarker, then first H1.
function findRootBoundaryFrom2029(markdown: string, marker2029Index: number): { rootContent: string; afterRoot: string } {
  const before2029 = markdown.substring(0, marker2029Index)
  const scenarioStartMatch = before2029.match(SCENARIO_2026_HEADING_REGEX)
  if (scenarioStartMatch && scenarioStartMatch.index !== undefined) {
    return { rootContent: before2029.substring(0, scenarioStartMatch.index), afterRoot: markdown.substring(scenarioStartMatch.index) }
  }
  const firstDateMarkerMatch = before2029.match(ANY_DATE_MARKER_REGEX)
  if (firstDateMarkerMatch && firstDateMarkerMatch.index !== undefined) {
    // No `## 2026…` scenario heading: the scenario opens at the first
    // dated `:::dateMarker` block (e.g. 2026-01-01). Everything before
    // it is the playbook preamble/root. (firstH1Match can't be used here
    // — the first H1 is `# 2029: …`, which sits *after* the 2029
    // dateMarker and would drop the 2026-2028 section entirely.)
    return { rootContent: before2029.substring(0, firstDateMarkerMatch.index), afterRoot: markdown.substring(firstDateMarkerMatch.index) }
  }
  const firstH1Match = markdown.match(/^# .+$/m)
  if (firstH1Match && firstH1Match.index !== undefined) {
    return { rootContent: markdown.substring(0, firstH1Match.index), afterRoot: markdown.substring(firstH1Match.index) }
  }
  throw new Error('Could not find :::timelineDiagram or first scenario heading')
}

// Returns the character index in `content` of the byte right after the line at
// `lineIndex` (i.e. the start of the next line, or content.length if at EOF).
function indexAfterLine(content: string, lineIndex: number): number {
  let pos = 0
  let line = 0
  while (line < lineIndex && pos < content.length) {
    const nl = content.indexOf('\n', pos)
    if (nl === -1) return content.length
    pos = nl + 1
    line++
  }
  // Advance past the line at lineIndex itself.
  const nl = content.indexOf('\n', pos)
  return nl === -1 ? content.length : nl + 1
}

// Finds the character index in `content` just past the close `:::` of the
// last `:::details ...Plan [SBCD]` block. Returns undefined if no plan-ending
// block parses cleanly. Mirrors parsePlanEndings' scan so the two stay aligned.
function findEndOfLastPlanEnding(content: string): number | undefined {
  const lines = content.split('\n')
  let lastCloseLine: number | undefined
  let i = 0
  while (i < lines.length) {
    if (!isDetailsOpener(lines[i].trim())) { i++; continue }
    let titleLineIdx = i + 1
    while (titleLineIdx < lines.length && lines[titleLineIdx].trim() === '') titleLineIdx++
    const titleLine = lines[titleLineIdx]?.trim() || ''
    if (!/(?:W)?hat happens in Plan [SBCD]/.test(titleLine)) { i++; continue }
    const { closeLine, closed } = findPlanEndingClose(lines, i, titleLineIdx)
    if (closed) lastCloseLine = closeLine
    i = closeLine + 1
  }
  if (lastCloseLine === undefined) return undefined
  return indexAfterLine(content, lastCloseLine)
}

// Splits the 2026-2028 region into three pieces: the 2026-2028 content, the
// "What happens in Plan…" plan-ending blocks, and any "orphan" content that
// sits after the last plan-ending block but before the next section's H1 (e.g.
// the 2029 epigraph quote + bold preamble paragraph, which in the Google Doc
// land between `:::` closing Plan D and `# 2029: …`). The orphan is returned
// so findDocBoundaries can prepend it to afterIntroSection, where it belongs.
function splitPlanEndingsOff(
  section2026_2028: string,
  warnings: string[],
): { section2026_2028: string; planEndingsContent: string; orphanAfterPlanEndings: string } {
  const planEndingsMarker = section2026_2028.match(/^:{3,}details\s*\n+\s*(?:W)?hat happens in Plan [SBCD]/m)
  if (!planEndingsMarker || planEndingsMarker.index === undefined) {
    warnings.push('Could not find "What happens in Plan" sections')
    return { section2026_2028, planEndingsContent: '', orphanAfterPlanEndings: '' }
  }
  const planEndingsStart = planEndingsMarker.index
  const fullPlanEndingsRegion = section2026_2028.substring(planEndingsStart)
  const endOfLastEnding = findEndOfLastPlanEnding(fullPlanEndingsRegion)
  const planEndingsContent = endOfLastEnding === undefined
    ? fullPlanEndingsRegion
    : fullPlanEndingsRegion.substring(0, endOfLastEnding)
  const orphanAfterPlanEndings = endOfLastEnding === undefined
    ? ''
    : fullPlanEndingsRegion.substring(endOfLastEnding)
  return {
    section2026_2028: section2026_2028.substring(0, planEndingsStart),
    planEndingsContent,
    orphanAfterPlanEndings,
  }
}

// Phase 1: locate root / 2026-2028 / plan-endings boundaries. The "What
// happens in Plan…" blocks are the anchor: the app renders the
// choose-your-path flowchart between the root node's content and the chosen
// branch, so everything the author placed above those blocks in the Google
// Doc (heading, dateMarker, epigraph…) stays in the 2026-2028 chunk and
// renders above the flowchart, and everything after them becomes the Plan A
// region. Doc order is render order — moving content above/below the plan
// blocks in the doc moves it above/below the flowchart.
export function findDocBoundaries(markdown: string, warnings: string[]): DocBoundaries {
  const { rootContent, afterRoot } = findRootBoundary(markdown)
  const { section2026_2028, planEndingsContent, orphanAfterPlanEndings } =
    splitPlanEndingsOff(afterRoot, warnings)
  if (!planEndingsContent) {
    return findBoundariesWithoutPlanEndings(rootContent, afterRoot, warnings)
  }
  // The Plan A region needs a leading H1 for the section-splitter. Older docs
  // open it with their own `# 2029: …` H1 (only an epigraph may precede it);
  // newer docs place that H1 above the plan-ending blocks instead, so a copy
  // is prepended here purely as the section boundary + title and later
  // stripped from the generated chunk via `hoistedSectionTitle`.
  const planARegion = orphanAfterPlanEndings
  if (regionOpensWithItsOwnH1(planARegion)) {
    return { rootContent, section2026_2028, afterIntroSection: planARegion, planEndingsContent }
  }
  const h1Matches = [...section2026_2028.matchAll(/^# 20(?:29|[3-9]\d)[\s:].+$/gm)]
  const lastH1 = h1Matches[h1Matches.length - 1]
  if (!lastH1 || lastH1.index === undefined) {
    warnings.push('Plan A region has no leading H1 and no `# 20xx` H1 sits above the plan-ending blocks to hoist')
    return { rootContent, section2026_2028, afterIntroSection: planARegion, planEndingsContent }
  }
  const hoistedH1Line = lastH1[0]
  const tailStart = tailStartIncludingDateMarker(section2026_2028, lastH1.index)
  return {
    rootContent,
    section2026_2028: section2026_2028.substring(0, tailStart),
    afterIntroSection: hoistedH1Line + '\n\n' + planARegion,
    planEndingsContent,
    section2026Tail: section2026_2028.substring(tailStart),
    hoistedSectionTitle: hoistedH1Line.replace(/^#\s+/, '').trim(),
  }
}

// Start of the 2026-2028 tail: the hoisted H1, extended backwards to include
// the `:::dateMarker` block that immediately precedes it (only blank lines
// between), so the date label travels with its heading.
function tailStartIncludingDateMarker(section2026_2028: string, h1Index: number): number {
  const beforeH1 = section2026_2028.substring(0, h1Index)
  const dateMarkerJustBefore = beforeH1.match(/:{3,}dateMarker[ \t]*\n(?:[^\n]+\n){1,3}:{3,}[ \t]*\n+$/)
  if (!dateMarkerJustBefore || dateMarkerJustBefore.index === undefined) return h1Index
  const atLineStart = dateMarkerJustBefore.index === 0
    || beforeH1[dateMarkerJustBefore.index - 1] === '\n'
  return atLineStart ? dateMarkerJustBefore.index : h1Index
}

// True when the first H1 in the region precedes any `:::directive` content —
// i.e. the region opens with its own section heading (the older doc layout,
// where `# 2029: …` sits after the plan-ending blocks with at most an
// epigraph before it).
function regionOpensWithItsOwnH1(region: string): boolean {
  const firstH1 = region.match(/^# .+$/m)
  if (!firstH1 || firstH1.index === undefined) return false
  const firstDirective = region.match(/^:{3,}[a-zA-Z]/m)
  if (!firstDirective || firstDirective.index === undefined) return true
  return firstH1.index < firstDirective.index
}

// Fallback when no "What happens in Plan…" blocks exist (splitPlanEndingsOff
// already warned): split at the 2029 H1 / dateMarker like before. Docs whose
// story starts later than 2029 (e.g. the intro runs straight into `# 2030: …`)
// have no 2029 dateMarker at all — for those, split at the first `# 20xx` H1
// (or first dated dateMarker) instead so they still resolve into branch
// sections rather than failing the whole split.
function findBoundariesWithoutPlanEndings(
  rootContent: string,
  afterRoot: string,
  warnings: string[],
): DocBoundaries {
  const dateMarker2029 = afterRoot.match(DATE_MARKER_2029_REGEX)
  if (!dateMarker2029 || dateMarker2029.index === undefined) {
    const firstYearH1 = afterRoot.match(/^# 20[2-9]\d[\s:].+$/m)
    const firstDateMarker = afterRoot.match(ANY_DATE_MARKER_REGEX)
    const fallbackIndex = firstYearH1?.index ?? firstDateMarker?.index
    if (fallbackIndex === undefined) {
      throw new Error('Could not find :::dateMarker for 2029')
    }
    warnings.push('No 2029 dateMarker; splitting sections at the first `# 20xx` H1 / dated dateMarker instead')
    return {
      rootContent,
      section2026_2028: afterRoot.substring(0, fallbackIndex),
      afterIntroSection: afterRoot.substring(fallbackIndex),
      planEndingsContent: '',
    }
  }
  // The H1 heading (e.g. `# 2029: …`) and its preceding content (e.g. an
  // epigraph quote) may appear before the dateMarker in the Google Doc. Move
  // the split point back to the H1 so it ends up in afterIntroSection.
  let splitIndex = dateMarker2029.index
  const textBeforeMarker = afterRoot.substring(0, dateMarker2029.index)
  const h1Before = textBeforeMarker.match(/^# 20(29|[3-9]\d)[\s:].+$/m)
  if (h1Before && h1Before.index !== undefined) {
    splitIndex = h1Before.index
  }
  return {
    rootContent,
    section2026_2028: afterRoot.substring(0, splitIndex),
    afterIntroSection: afterRoot.substring(splitIndex),
    planEndingsContent: '',
  }
}

// Branchpoint blocks are no longer extracted from the markdown — they render
// in place as inline expandable boxes via the runtime `:::branchpoint`
// directive handler. The only thing the parser needs to do is make sure
// nested directives (notably `:::branchSummary`) parse correctly at runtime
// by giving the outer `:::branchpoint` a deeper fence than its contents.

type BranchpointFenceSpan = { start: number; end: number; openerFenceLength: number }

// Locates the matching close-line for the branchpoint opening at `i`, using
// the depth-counting scanner so a same-fence-length `:::` from a nested
// directive doesn't terminate the outer block early.
function findBranchpointSpan(lines: string[], i: number): BranchpointFenceSpan | undefined {
  const openMatch = lines[i].trim().match(/^(:{3,})branchpoint$/)
  if (!openMatch) return undefined
  const { closeLine: j, closed } = scanDirectiveDepthClose(lines, i + 1, isAnyDirectiveOpener)
  if (!closed) return undefined
  return { start: i, end: j, openerFenceLength: openMatch[1].length }
}

// Re-fences the branchpoint span at [start, end] so its outer fence is longer
// than any directive fence appearing inside. Required for remark-directive to
// nest a same-length `:::branchSummary` (or similar) under it at runtime.
function repairBranchpointFence(lines: string[], span: BranchpointFenceSpan): void {
  const inner = lines.slice(span.start + 1, span.end)
  let maxInnerFence = 0
  for (const line of inner) {
    const trimmed = line.trim()
    const m = trimmed.match(/^(:{3,})(?:[a-zA-Z][a-zA-Z0-9_-]*)?$/)
    if (m && m[1].length > maxInnerFence) maxInnerFence = m[1].length
  }
  const targetFenceLength = Math.max(span.openerFenceLength, maxInnerFence + 1)
  if (targetFenceLength === span.openerFenceLength) return
  const fence = ':'.repeat(targetFenceLength)
  lines[span.start] = `${fence}branchpoint`
  lines[span.end] = fence
}

// Walks the line array once, repairing every top-level `:::branchpoint`
// outer fence so its nested directives can nest correctly at parse time.
export function repairBranchpointFences(content: string): string {
  const lines = content.split('\n')
  let i = 0
  let changed = false
  while (i < lines.length) {
    const span = findBranchpointSpan(lines, i)
    if (!span) { i++; continue }
    const beforeOpen = lines[span.start]
    repairBranchpointFence(lines, span)
    if (lines[span.start] !== beforeOpen) changed = true
    i = span.end + 1
  }
  return changed ? lines.join('\n') : content
}

const SECTION_BOUNDARY_REGEX = /^(?::::dateMarker[ \t]*\n(?:[^\n]+\n){1,3}:::[ \t]*\n+)?^# .+$/gm

// Phase 2a: slices the Plan A region into `# Heading`-delimited sections.
// We constrain the optional preceding dateMarker block body to 1-3 lines so
// the lazy quantifier can't swallow intermediate H1 boundaries.
export function parsePlanASections(planAContent: string): Section[] {
  const boundaryPositions: { index: number; fullMatch: string }[] = []
  let bm: RegExpExecArray | null
  SECTION_BOUNDARY_REGEX.lastIndex = 0
  while ((bm = SECTION_BOUNDARY_REGEX.exec(planAContent)) !== null) {
    boundaryPositions.push({ index: bm.index, fullMatch: bm[0] })
  }
  return boundaryPositions.map((bp, i) => sectionFromBoundary(planAContent, boundaryPositions, bp, i)).filter((s): s is Section => s !== undefined)
}

// One section spanning from its boundary to the next boundary (or end).
// The very first section absorbs any pre-H1 content at the start of
// `planAContent` (e.g. the 2029 epigraph + bold preamble that the doc places
// between the last plan-ending close and the `# 2029:` H1) — without this,
// content before boundaries[0].index would be silently dropped.
function sectionFromBoundary(
  planAContent: string,
  boundaries: { index: number; fullMatch: string }[],
  bp: { index: number; fullMatch: string },
  i: number,
): Section | undefined {
  const titleMatch = bp.fullMatch.match(/^# (.+)$/m)
  if (!titleMatch) return undefined
  const start = i === 0 ? 0 : bp.index
  const end = i + 1 < boundaries.length ? boundaries[i + 1].index : planAContent.length
  return { title: titleMatch[1].trim(), content: planAContent.substring(start, end) }
}

// Phase 2b: where a section also contains a `# Epilogue…` H1, split it so the
// epilogue becomes its own section.
export function splitEpilogues(planASections: Section[]): Section[] {
  const out: Section[] = []
  for (const section of planASections) splitOneEpilogue(section, out)
  return out
}

// Splits one section around its `# Epilogue…` heading, if it has a real one.
function splitOneEpilogue(section: Section, out: Section[]): void {
  const epilogueHeadingMatch = section.content.match(/^# (Epilogue.*)$/m)
  if (!epilogueHeadingMatch) { out.push(section); return }
  const epilogueIndex = section.content.indexOf(`# ${epilogueHeadingMatch[1]}`)
  if (epilogueIndex <= 0) { out.push(section); return }
  const contentBeforeEpilogue = section.content.slice(0, epilogueIndex).replace(/\n+$/, '\n')
  if (contentBeforeEpilogue.trim()) {
    out.push({ title: section.title, content: contentBeforeEpilogue })
  }
  out.push({ title: epilogueHeadingMatch[1].trim(), content: section.content.slice(epilogueIndex) })
}

// Phase 3 (formerly): used to extract branchpoint blocks into separate stub
// nodes and split sections at section-splitting branchpoints. Now that
// branchpoints render in place as inline expandable boxes (see the runtime
// `BranchpointBox` directive handler), this phase only repairs the outer
// branchpoint fence so a same-length nested directive parses correctly at
// runtime. Sections pass through unchanged and the returned `branchpoints`
// array is always empty.
export function handleBranchpointsAndSplits(
  sections: Section[],
  _warnings: string[],
): { sections: Section[]; branchpoints: Branchpoint[] } {
  const repairedSections: Section[] = sections.map(s => ({
    ...s,
    content: repairBranchpointFences(s.content),
  }))
  return { sections: repairedSections, branchpoints: [] }
}

// Phase 4: parse the "What happens in Plan S/B/C/D" ending blocks.
export function parsePlanEndings(planEndingsContent: string): PlanEnding[] {
  const planEndings: PlanEnding[] = []
  if (!planEndingsContent) return planEndings
  const peLines = planEndingsContent.split('\n')
  let i = 0
  while (i < peLines.length) {
    const ending = parseOnePlanEnding(peLines, i)
    if (ending) { planEndings.push(ending.ending); i = ending.nextLine; continue }
    i++
  }
  return planEndings
}

// Parses one `:::details` plan-ending block starting at line `i`, if it is one.
function parseOnePlanEnding(peLines: string[], i: number): { ending: PlanEnding; nextLine: number } | undefined {
  if (!isDetailsOpener(peLines[i].trim())) return undefined
  let titleLineIdx = i + 1
  while (titleLineIdx < peLines.length && peLines[titleLineIdx].trim() === '') titleLineIdx++
  const titleLine = peLines[titleLineIdx]?.trim() || ''
  const planMatch = titleLine.match(/(?:W)?hat happens in Plan ([SBCD])/)
  if (!planMatch) return undefined
  const { closeLine: j } = findPlanEndingClose(peLines, i, titleLineIdx)
  const ending: PlanEnding = {
    planLetter: planMatch[1],
    title: titleLine,
    content: peLines.slice(titleLineIdx + 1, j).join('\n'),
  }
  return { ending, nextLine: j + 1 }
}
